<template>
    <BaseWidget v-bind="$attrs" :widget="meta" :loading="loading">
        <div class="dw-weather" :class="'dw-weather--' + condClass">
            <div class="dw-weather-fx">
                <span v-for="n in effectCount" :key="n"></span>
            </div>
            <div class="dw-weather-content">
                <div class="dw-weather-icon-area">
                    <div class="dw-weather-svg" v-html="svg"></div>
                </div>
                <div class="dw-weather-main">
                    <span class="dw-weather-temp">{{ tempDisplay }}°</span>
                    <span class="dw-weather-feels">Hissedilen {{ feelsDisplay }}°</span>
                </div>
                <div class="dw-weather-meta">
                    <span class="dw-weather-city">{{ cityDisplay }}</span>
                    <span class="dw-weather-desc">{{ descDisplay }}</span>
                </div>
                <div class="dw-weather-details">
                    <div class="dw-weather-detail"><i class="bi bi-droplet-half"></i><span>%{{ humidityDisplay }}</span></div>
                    <div class="dw-weather-detail-sep"></div>
                    <div class="dw-weather-detail"><i class="bi bi-wind"></i><span>{{ windDisplay }} km/h</span></div>
                    <div class="dw-weather-detail-sep"></div>
                    <div class="dw-weather-detail"><i class="bi bi-clock"></i><span>{{ timeDisplay }}</span></div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script>
import BaseWidget from '../BaseWidget.vue';
import widgetMixin from '../widgetMixin';

const SVGS = {
    clear: '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="14" fill="currentColor" opacity="0.9"/><g stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="32" y1="4" x2="32" y2="12"/><line x1="32" y1="52" x2="32" y2="60"/><line x1="4" y1="32" x2="12" y2="32"/><line x1="52" y1="32" x2="60" y2="32"/><line x1="12.2" y1="12.2" x2="17.9" y2="17.9"/><line x1="46.1" y1="46.1" x2="51.8" y2="51.8"/><line x1="12.2" y1="51.8" x2="17.9" y2="46.1"/><line x1="46.1" y1="17.9" x2="51.8" y2="12.2"/></g></svg>',
    cloudy: '<svg viewBox="0 0 64 64" fill="none"><circle cx="24" cy="24" r="11" fill="currentColor" opacity="0.7"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.5"><line x1="24" y1="4" x2="24" y2="10"/><line x1="24" y1="38" x2="24" y2="44"/><line x1="4" y1="24" x2="10" y2="24"/><line x1="38" y1="24" x2="44" y2="24"/></g><path d="M46 38a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 38z" fill="currentColor" opacity="0.5"/></svg>',
    overcast: '<svg viewBox="0 0 64 64" fill="none"><path d="M48 34a10 10 0 0 1 0 20H16a8 8 0 0 1 0-16h1a10 10 0 0 1 19.2-3.2A10 10 0 0 1 48 34z" fill="currentColor" opacity="0.5"/><path d="M40 24a7 7 0 0 1 0 14H22a5 5 0 0 1 0-10h.5a7 7 0 0 1 13.5-2.2A7 7 0 0 1 40 24z" fill="currentColor" opacity="0.3"/></svg>',
    rain: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 22a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 22z" fill="currentColor" opacity="0.5"/><g stroke="currentColor" stroke-width="2.5" stroke-linecap="round" opacity="0.6"><line x1="20" y1="44" x2="16" y2="54"/><line x1="30" y1="44" x2="26" y2="54"/><line x1="40" y1="44" x2="36" y2="54"/><line x1="48" y1="44" x2="44" y2="54"/></g></svg>',
    snow: '<svg viewBox="0 0 64 64" fill="none"><path d="M46 20a9 9 0 0 1 0 18H18a7 7 0 0 1 0-14h1a9 9 0 0 1 17.4-2.8A9 9 0 0 1 46 20z" fill="currentColor" opacity="0.5"/><g fill="currentColor" opacity="0.7"><circle cx="18" cy="44" r="2"/><circle cx="28" cy="48" r="1.5"/><circle cx="38" cy="43" r="2"/><circle cx="48" cy="47" r="1.5"/><circle cx="23" cy="54" r="1.5"/><circle cx="43" cy="55" r="2"/><circle cx="33" cy="57" r="1.5"/></g></svg>',
    night_clear: '<svg viewBox="0 0 64 64" fill="none"><path d="M38 12a22 22 0 1 0 14 38A22 22 0 0 1 38 12z" fill="currentColor" opacity="0.85"/><circle cx="48" cy="14" r="1.5" fill="currentColor" opacity="0.4"/><circle cx="54" cy="24" r="1" fill="currentColor" opacity="0.3"/><circle cx="44" cy="8" r="1" fill="currentColor" opacity="0.35"/></svg>',
};

const EFFECTS = {
    clear: 3, cloudy: 3, overcast: 3, rain: 8, snow: 8, night_clear: 4,
};

const ICON_MAP = {
    clear: 'clear',
    mainly_clear: 'clear',
    sunny: 'clear',
    partly_cloudy: 'cloudy',
    cloudy: 'cloudy',
    overcast: 'overcast',
    rain: 'rain',
    drizzle: 'rain',
    showers: 'rain',
    thunderstorm: 'rain',
    snow: 'snow',
    sleet: 'snow',
    night: 'night_clear',
    clear_night: 'night_clear',
    night_clear: 'night_clear',
};

function mapCondition(iconKey) {
    if (!iconKey) return 'clear';
    return ICON_MAP[iconKey] || 'clear';
}

export const meta = {
    id: 'weather',
    titleKey: 'common.dashboard.widgetTitles.weather',
    icon: 'bi-cloud-sun',
    accent: 'var(--bs-info)',
    accentBg: 'rgba(var(--bs-info-rgb, 114, 57, 234), 0.12)',
    glassRgb: '114, 57, 234',
    defaultCol: 1,
    defaultRow: 1,
    multiInstance: true,
    source: { type: 'module', moduleCode: 'WEATHER', code: 'CURRENT_WEATHER' },
    defaultConfig: { city_id: null, district_id: null, location_label: '' },
    configFields: [
        { key: 'location', label: 'common.dashboard.widgetFields.location', type: 'weather_location' },
    ],
    buildParams(config) {
        const p = {};
        if (config?.city_id) p.city_id = config.city_id;
        if (config?.district_id) p.district_id = config.district_id;
        return Object.keys(p).length ? p : null;
    },
};

export default {
    name: 'WeatherWidget',
    components: { BaseWidget },
    mixins: [widgetMixin],
    data() {
        return {
            meta,
            now: new Date(),
        };
    },
    created() {
        this._clockTimer = null;
    },
    mounted() {
        this._clockTimer = setInterval(() => { this.now = new Date(); }, 30000);
    },
    beforeUnmount() {
        if (this._clockTimer) clearInterval(this._clockTimer);
    },
    computed: {
        condClass() {
            return mapCondition(this.apiData?.condition?.icon);
        },
        svg() {
            return SVGS[this.condClass] || SVGS.clear;
        },
        effectCount() {
            return EFFECTS[this.condClass] || 0;
        },
        tempDisplay() {
            const t = this.apiData?.temperature;
            return t == null ? '—' : Math.round(t);
        },
        feelsDisplay() {
            const t = this.apiData?.feels_like;
            return t == null ? '—' : Math.round(t);
        },
        cityDisplay() {
            if (!this.apiData) return '—';
            const c = this.apiData.city || '';
            const d = this.apiData.district;
            return d ? `${d}, ${c}` : c;
        },
        descDisplay() {
            return this.apiData?.condition?.label || '';
        },
        humidityDisplay() {
            return this.apiData?.humidity ?? '—';
        },
        windDisplay() {
            const w = this.apiData?.wind_speed;
            return w == null ? '—' : Math.round(w);
        },
        timeDisplay() {
            const h = String(this.now.getHours()).padStart(2, '0');
            const m = String(this.now.getMinutes()).padStart(2, '0');
            return `${h}:${m}`;
        },
    },
};
</script>
